import React, { useState } from 'react';
import { Flex } from '@chakra-ui/react';
import {
  FormFieldClinicalTrials,
  FormFieldEthicsStatement
} from '@publishing-engine/domain/types/article';
import { IDependentValue } from '@publishing-engine/domain/models/submission';
import { DependentTextUpdateModal } from './DependentTextUpdateModal';
import { FieldHeader } from 'src/components/Submission/GenericFieldComponents/common/FieldHeader';
import { TextEmptyState } from '../Text/TextEmptyState';
import { TextDisplay } from '../Text/TextDisplay';
import { DependentTextDiffModal } from './DependentTextDiffModal';
import { IAvailableWorkflowActions } from '@publishing-engine/domain/models';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';
import { EntryPoint } from 'src/utils/types';

// TODO co-locate with other form field types.
// TODO specify union of all known text component types?
export type FormFieldDependentText = Omit<
  FormFieldEthicsStatement | FormFieldClinicalTrials,
  'component'
> & {
  component: string;
};

type IProps = {
  formFieldDefinition: FormFieldDependentText;
  value?: IDependentValue<string>;
  previousValue?: IDependentValue<string>;
  availableActions: IAvailableWorkflowActions;
  onChange: (value: IDependentValue<string>) => void;
  entryPoint: EntryPoint;
  isUpdateRequired?: boolean;
};

export const DependentTextField: React.FC<IProps> = ({
  formFieldDefinition,
  value,
  previousValue,
  availableActions,
  onChange,
  entryPoint,
  isUpdateRequired
}) => {
  const { checkFormFieldValueIsUpdated } = useSubmissionFields(entryPoint);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const isEditable = !!onChange;
  const [showDiffModal, setShowDiffModal] = useState<boolean>(false);
  const openDiffModal = () => setShowDiffModal(true);
  const closeDiffModal = () => setShowDiffModal(false);

  const editCallback = isEditable
    ? () => {
        setIsEditing(true);
      }
    : undefined;

  const isUpdated = checkFormFieldValueIsUpdated(formFieldDefinition.name);
  return (
    <Flex flexDirection={'column'} gap={2}>
      {isEditing && (
        <DependentTextUpdateModal
          formField={formFieldDefinition}
          isOpen={isEditing}
          onClose={() => {
            setIsEditing(false);
          }}
          value={value}
          onSubmit={value => {
            setIsEditing(false);
            onChange(value);
          }}
        />
      )}

      {isUpdated && showDiffModal ? (
        <DependentTextDiffModal
          config={formFieldDefinition}
          value={value}
          previousValue={previousValue}
          onClose={closeDiffModal}
        />
      ) : null}

      <FieldHeader
        {...formFieldDefinition}
        label={formFieldDefinition.params.labels.main}
        isUpdated={isUpdated}
        editCallback={editCallback}
        onShowDiffModal={openDiffModal}
        availableActions={availableActions}
        entryPoint={entryPoint}
        isUpdateRequired={isUpdateRequired}
      />

      {value ? (
        <>
          <TextDisplay
            value={
              formFieldDefinition?.params?.labels?.options[`${value.exists}`]
                ?.main ?? ''
            }
          />
          {value.value && <TextDisplay value={value.value} />}
        </>
      ) : (
        <TextEmptyState />
      )}
    </Flex>
  );
};
